import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronLeft, ShieldCheck, Send, MessageCircle, Clock,
  CheckCircle, Filter, User, ExternalLink, Loader2,
  Heart, Frown, HeartHandshake, Users, Sparkles, HelpCircle
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { value: "love", label: "연애 고민", icon: Heart },
  { value: "breakup", label: "이별/재회", icon: Frown },
  { value: "marriage", label: "결혼/재혼", icon: HeartHandshake },
  { value: "loneliness", label: "외로움", icon: Users },
  { value: "self", label: "자기개발", icon: Sparkles },
  { value: "general", label: "기타", icon: HelpCircle },
];

const getCatLabel = (v) => CATEGORIES.find(c => c.value === v)?.label || "기타";

export default function AdminCounselingPage() {
  const navigate = useNavigate();
  const { isAuthenticated, user, loading, token } = useAuth();
  const [posts, setPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [expandedId, setExpandedId] = useState(null);
  const [replyTexts, setReplyTexts] = useState({});
  const [replyingId, setReplyingId] = useState(null);
  const [successId, setSuccessId] = useState(null);

  useEffect(() => {
    if (loading) return;
    if (!isAuthenticated || user?.role !== "admin") {
      navigate("/");
      return;
    }
    fetchPosts();
  }, [loading, isAuthenticated, user, statusFilter]);

  const fetchPosts = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/api/private-counseling/admin/posts?status=${statusFilter}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        const data = await res.json();
        setPosts(data.posts || []);
      }
    } catch (e) {
      console.error(e);
    }
    setIsLoading(false);
  }, [token, statusFilter]);

  const handleReply = async (postId) => {
    const content = replyTexts[postId]?.trim();
    if (!content) return;
    setReplyingId(postId);
    try {
      const res = await fetch(`${API_URL}/api/private-counseling/posts/${postId}/reply`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ content }),
      });
      if (res.ok) {
        setReplyTexts(prev => ({ ...prev, [postId]: "" }));
        setSuccessId(postId);
        setTimeout(() => setSuccessId(null), 3000);
        fetchPosts();
      } else {
        const data = await res.json().catch(() => ({}));
        alert(data.detail || "답변 등록에 실패했습니다.");
      }
    } catch {
      alert("답변 등록에 실패했습니다.");
    }
    setReplyingId(null);
  };

  const getRelativeTime = (dateStr) => {
    if (!dateStr) return "";
    const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 60000);
    if (diff < 1) return "방금 전";
    if (diff < 60) return `${diff}분 전`;
    if (diff < 1440) return `${Math.floor(diff / 60)}시간 전`;
    return `${Math.floor(diff / 1440)}일 전`;
  };

  const openCount = posts.filter(p => p.status === "open").length;
  const answeredCount = posts.filter(p => p.status === "answered").length;

  if (loading) return null;

  return (
    <div className="min-h-screen bg-[#FAFAF9] pt-20">
      <div className="max-w-5xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate("/admin")} data-testid="back-to-admin">
              <ChevronLeft className="w-4 h-4 mr-1" /> 대시보드
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-stone-800 flex items-center gap-2">
                <ShieldCheck className="w-6 h-6 text-emerald-500" />
                비공개 상담 관리
              </h1>
              <p className="text-sm text-stone-500 mt-0.5">접수된 익명 상담에 답변하고 관리합니다</p>
            </div>
          </div>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-xl border border-stone-100 p-4 shadow-sm" data-testid="stat-total">
            <p className="text-xs text-stone-500">전체 접수</p>
            <p className="text-2xl font-bold text-stone-800">{posts.length}</p>
          </div>
          <div className="bg-white rounded-xl border border-amber-100 p-4 shadow-sm" data-testid="stat-open">
            <p className="text-xs text-amber-600">답변 대기</p>
            <p className="text-2xl font-bold text-amber-600">{openCount}</p>
          </div>
          <div className="bg-white rounded-xl border border-emerald-100 p-4 shadow-sm" data-testid="stat-answered">
            <p className="text-xs text-emerald-600">답변 완료</p>
            <p className="text-2xl font-bold text-emerald-600">{answeredCount}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2 mb-5">
          <Filter className="w-4 h-4 text-stone-400" />
          {[
            { val: "all", label: "전체" },
            { val: "open", label: "답변 대기" },
            { val: "answered", label: "답변 완료" },
          ].map((f) => (
            <button
              key={f.val}
              onClick={() => setStatusFilter(f.val)}
              className={`px-3 py-1.5 text-xs rounded-full font-medium transition-colors ${
                statusFilter === f.val
                  ? "bg-stone-800 text-white"
                  : "bg-white text-stone-600 border border-stone-200 hover:bg-stone-50"
              }`}
              data-testid={`filter-${f.val}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Posts List */}
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-stone-400" />
          </div>
        ) : posts.length === 0 ? (
          <div className="bg-white rounded-xl border border-stone-100 p-16 text-center shadow-sm">
            <ShieldCheck className="w-10 h-10 text-stone-300 mx-auto mb-3" />
            <p className="text-stone-400">접수된 상담이 없습니다</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="counseling-posts-list">
            {posts.map((post) => {
              const isExpanded = expandedId === post.id;
              const isAnswered = post.status === "answered";
              const isSuccess = successId === post.id;
              return (
                <div
                  key={post.id}
                  className={`bg-white rounded-xl border shadow-sm overflow-hidden transition-all ${
                    isAnswered ? "border-emerald-100" : "border-amber-200"
                  }`}
                  data-testid={`admin-post-${post.id}`}
                >
                  {/* Post Header - click to expand */}
                  <div
                    className="p-5 cursor-pointer hover:bg-stone-50/50 transition-colors"
                    onClick={() => setExpandedId(isExpanded ? null : post.id)}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-xs px-2 py-0.5 rounded-full bg-stone-100 text-stone-500">
                            {getCatLabel(post.category)}
                          </span>
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                            isAnswered ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"
                          }`}>
                            {isAnswered ? "답변 완료" : "답변 대기"}
                          </span>
                          {isSuccess && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 animate-pulse">
                              전송됨!
                            </span>
                          )}
                        </div>
                        <h3 className="font-semibold text-stone-800">{post.title}</h3>
                        <div className="flex items-center gap-3 mt-1.5 text-xs text-stone-400">
                          <span className="flex items-center gap-1"><User className="w-3.5 h-3.5" />{post.anonymous_name}</span>
                          <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{getRelativeTime(post.created_at)}</span>
                          <span className="flex items-center gap-1"><MessageCircle className="w-3.5 h-3.5" />답변 {post.replies_count || 0}</span>
                        </div>
                      </div>
                      <div className={`text-xs px-2 py-1 rounded ${isExpanded ? "bg-stone-800 text-white" : "bg-stone-100 text-stone-500"}`}>
                        {isExpanded ? "접기" : "펼치기"}
                      </div>
                    </div>
                  </div>

                  {/* Expanded Content */}
                  {isExpanded && (
                    <div className="border-t border-stone-100">
                      {/* Question */}
                      <div className="p-5 bg-stone-50">
                        <p className="text-xs text-stone-500 mb-2 font-medium">상담 내용</p>
                        <p className="text-sm text-stone-700 leading-relaxed whitespace-pre-wrap">{post.content}</p>
                      </div>

                      {/* Existing Replies */}
                      {post.replies && post.replies.length > 0 && (
                        <div className="p-5 space-y-3">
                          <p className="text-xs text-stone-500 font-medium">이전 답변</p>
                          {post.replies.map((reply) => (
                            <div key={reply.id} className="bg-emerald-50 border border-emerald-100 rounded-lg p-4">
                              <div className="flex items-center gap-2 mb-2">
                                <CheckCircle className="w-4 h-4 text-emerald-500" />
                                <span className="text-sm font-semibold text-emerald-800">{reply.coach_name}</span>
                                <span className="text-xs text-stone-400">{getRelativeTime(reply.created_at)}</span>
                              </div>
                              <p className="text-sm text-stone-700 whitespace-pre-wrap">{reply.content}</p>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Reply Form */}
                      <div className="p-5 bg-white border-t border-stone-100">
                        <p className="text-xs text-stone-500 font-medium mb-2">답변 작성</p>
                        <textarea
                          value={replyTexts[post.id] || ""}
                          onChange={(e) => setReplyTexts(prev => ({ ...prev, [post.id]: e.target.value }))}
                          placeholder="따뜻한 답변을 작성해주세요. 답변은 작성자의 메시지함으로 전달됩니다."
                          className="w-full h-32 px-4 py-3 border border-stone-200 rounded-xl text-sm resize-none focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:border-emerald-300"
                          data-testid={`reply-textarea-${post.id}`}
                        />
                        <div className="flex items-center justify-between mt-3">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate("/counseling")}
                            className="text-stone-600 border-stone-300 hover:bg-stone-50"
                            data-testid={`liz-counseling-btn-${post.id}`}
                          >
                            <ExternalLink className="w-4 h-4 mr-1.5" />
                            리즈 상담 신청 안내
                          </Button>
                          <Button
                            size="sm"
                            className="bg-emerald-600 hover:bg-emerald-700 text-white px-6"
                            onClick={() => handleReply(post.id)}
                            disabled={replyingId === post.id || !(replyTexts[post.id]?.trim())}
                            data-testid={`reply-submit-${post.id}`}
                          >
                            {replyingId === post.id ? (
                              <><Loader2 className="w-4 h-4 animate-spin mr-1.5" />전송 중...</>
                            ) : (
                              <><Send className="w-4 h-4 mr-1.5" />답변완료</>
                            )}
                          </Button>
                        </div>
                        <p className="text-[11px] text-stone-400 mt-2">답변은 작성자의 메시지함으로 자동 전달됩니다.</p>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
